import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { NativeSelect } from "@mantine/core";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { CompanyProperty, Project, Property } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { absent, orAbsent, place, propertyType, roleWords, shortDate } from "../lib/format";
import { applySorts, useListParams } from "../lib/useListParams";
import { csvFilename, downloadCsv, toCsv } from "../lib/csv";
import { LookupFacts } from "../components/Enrichment";
import { ActivityTable } from "../components/ActivityTable";
import { CampaignsTable, campaignCount, useRecordCampaigns } from "../components/CampaignsTable";
import { RecordLink } from "../components/RecordLink";
import { Spinner } from "../components/Spinner";
import { FlagFacts } from "../components/FlagRecord";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { EditBar, Fact, FactGroup, Facts, useContestedFacts } from "../ui/Facts";
import { useRecordEdit } from "../lib/useRecordEdit";
import { RecordPage, useRecordNav } from "../ui/RecordPage";
import { Stage, useStageOrder } from "../ui/Stage";
import { FiltersPanel, Toolbar, useFiltersOpen, type Chip } from "../ui/Toolbar";
import { ReachCell, StandingWord, Word } from "../ui/words";
import { campaignPath } from "../lib/campaign";
import { campaignWords, useCampaignLabels } from "../lib/campaignLabels";

/* One property, as a page.
 *
 * The design pass, 2026-10-08. A building is where Buzze's work lands, so
 * the question the top of the page answers is what this building is and
 * what is happening on it: the name and address, the type and size, whether
 * it has chargers, and the projects in flight. Details is the first tab,
 * with the facts in three groups: where it is, the building, and what EVIE
 * knows. Then the projects, who owns and manages it and who to call about
 * it, as tables, each with its state in the URL under its own prefix.
 * Projects are here rather than on a screen of their own, which is the cut
 * James made on 2026-09-10. */

type Tool = "edit" | "lookup" | "flag" | null;

const safe = (q: string) => q.replace(/[,()*\\]/g, " ").trim();

function yesNo(v: boolean | null | undefined, unknown = "unknown") {
  if (v === null || v === undefined) return absent(unknown);
  return v ? "yes" : "no";
}

export function PropertyPage({ session }: { session: SignedIn }) {
  const { id = "" } = useParams();
  const token = session.idToken;
  const [tool, setTool] = useState<Tool>(null);

  const property = useQuery({
    queryKey: ["property", id],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("property").select("*").eq("id", id).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as Property | null;
    },
  });

  const openFlags = useQuery({
    queryKey: ["flags-open", "property", id],
    queryFn: async () => {
      const { error, count } = await apiClient(token)
        .from("record_flag").select("id", { count: "exact", head: true })
        .eq("entity_type", "property").eq("entity_id", id).is("resolved_at", null);
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });

  const campaigns = useRecordCampaigns(token, "property", id);

  const p = property.data;
  const chargers = p?.has_existing_ev_chargers;

  return (
    <RecordPage
      backLabel="Back to properties" backTo="/properties" fromKey="properties"
      loading={property.isLoading}
      loadedAt={property.dataUpdatedAt} refreshing={property.isFetching} onRefresh={() => property.refetch()}
      error={property.isError ? (property.error as Error).message : null}
      notFound={!p && !property.isLoading ? "No property with that id. It may have been merged." : null}
      // No "also known as" line: lake.entity_name's check constraint allows
      // company and person only, so a property has one name and no others.
      title={p?.name ?? p?.address_line1 ?? "Property"}
      facts={p ? (
        <>
          {/* An address can be absent and the property still real, because a
              filing routinely precedes an address. Absence is a value. */}
          {p.address_line1 ?? <span className="muted">no street address on record</span>}
          {" · "}{place(p.city, p.state)}
          {p.jurisdiction_name ? <span className="muted">{" · "}{p.jurisdiction_name}</span> : null}
        </>
      ) : null}
      badges={p ? (
        <>
          {p.property_type && p.property_type !== "unknown"
            ? <Badge tone="plain">{propertyType(p.property_type)}</Badge> : null}
          {chargers === true ? <Badge tone="plain">Has chargers</Badge>
            : chargers === false ? <Badge tone="quiet">No chargers on record</Badge>
            : <Badge tone="quiet" title="Whether this property has chargers is not on record">Chargers unknown</Badge>}
          {p.affordable_flag ? <Badge tone="plain">Affordable</Badge> : null}
          {openFlags.data ? <Badge tone="waiting" title="Somebody has said this record is wrong">Flagged ({openFlags.data})</Badge> : null}
        </>
      ) : null}
      actions={[
        { label: "Edit", tab: "details", onSelect: () => setTool("edit") },
        { label: "Run web enrichment", tab: "details", anchor: "lookup", onSelect: () => setTool("lookup") },
        { label: "Flag this record", tab: "details", anchor: "flag", onSelect: () => setTool("flag") },
      ]}
      figures={[
        { n: p?.unit_count, label: "units" },
        { n: p?.project_count, label: "projects", tab: "projects" },
        { n: p?.stakeholder_count, label: "companies", tab: "companies" },
        { n: campaignCount(campaigns.data), label: "campaigns", tab: "campaigns", pending: campaigns.isPending },
        { n: p?.source_count, label: "sources", tab: "activity" },
      ]}
      defaultTab="details"
      tabs={[
        { key: "details", label: "Details", content: p ? <DetailsTab token={token} id={id} property={p} tool={tool} setTool={setTool} onChanged={() => property.refetch()} /> : null },
        { key: "projects", label: "Projects", count: p?.project_count, content: <ProjectsTab token={token} id={id} /> },
        { key: "companies", label: "Companies", count: p?.stakeholder_count, content: <CompaniesTab token={token} id={id} /> },
        { key: "people", label: "Who to call", content: <PeopleTab token={token} id={id} /> },
        { key: "campaigns", label: "Campaigns", count: campaignCount(campaigns.data), content: <CampaignsTable token={token} entityType="property" entityId={id} /> },
        { key: "activity", label: "Activity", count: p?.source_count, content: <ActivityTable token={token} entityType="property" entityId={id} /> },
      ]}
    />
  );
}

/* The record's facts, in three groups: where the building is, what it is,
   and what EVIE's data on it looks like. Edit, among the actions, turns the
   thirteen the engine lets a person correct (api.editable_field: the
   address and its parts, the parcel and tract, the name, type, units,
   parking, chargers and affordable) into inputs, with one note and one
   Save. The web lookup and the flag sit under what EVIE knows and step
   aside while editing. */
function DetailsTab({ token, id, property: p, tool, setTool, onChanged }: {
  token: string; id: string; property: Property; tool: Tool; setTool: (t: Tool) => void; onChanged: () => void;
}) {
  const editing = tool === "edit";
  const contested = useContestedFacts(token, "property", id);
  const [saved, setSaved] = useState<string[]>([]);
  const edit = useRecordEdit({
    token, entityType: "property", entityId: id,
    values: p as unknown as Record<string, unknown>,
    onSaved: (result) => {
      setSaved(result.fields);
      window.setTimeout(() => setSaved([]), 4000);
      setTool(null);
      onChanged();
    },
  });
  const toolProps = (t: Exclude<Tool, null | "edit">) => ({ open: tool === t, onOpenChange: (o: boolean) => setTool(o ? t : null) });
  return (
    <section className="rp-tabbody" aria-label="Details">
      {editing ? (
        <EditBar pending={edit.pending} saving={edit.saving} error={edit.error}
                 note={edit.note} onNote={edit.setNote} onSave={edit.save}
                 onCancel={() => { edit.reset(); setTool(null); }} />
      ) : null}
      <Facts token={token} entityType="property" values={p as unknown as Record<string, unknown>}
             editing={editing} draft={edit.draft} onDraft={edit.setField}
             contested={contested.data ?? {}} onUseClaim={edit.applyClaim} saved={saved}>
        <FactGroup title="Where it is">
          <Fact label="Address" field="address_line1">{p.address_line1 ?? absent("no street address on record")}</Fact>
          <Fact label="City" field="city">{orAbsent(p.city)}</Fact>
          <Fact label="State" field="state">{orAbsent(p.state)}</Fact>
          <Fact label="ZIP" field="zip">{orAbsent(p.zip)}</Fact>
          <Fact label="County" field="county">{orAbsent(p.county)}</Fact>
          <Fact label="Jurisdiction">{orAbsent(p.jurisdiction_name)}</Fact>
          <Fact label="Parcel" field="parcel_id">{orAbsent(p.parcel_id)}</Fact>
          <Fact label="Census tract" field="census_tract">{orAbsent(p.census_tract)}</Fact>
          <Fact label="Coordinates">
            {/* Precision travels with the coordinates. A parcel centroid and a
                rooftop geocode are different facts, and a pin that does not
                say which is a claim the lake never made. */}
            {p.lat && p.lon ? `${p.lat}, ${p.lon}` : absent()}
            {p.geocode_precision ? <span className="muted"> ({p.geocode_precision})</span> : null}
          </Fact>
          <Fact label="Coordinates added">{shortDate(p.geocoded_at)}</Fact>
        </FactGroup>

        <FactGroup title="The property">
          <Fact label="Name" field="name">{p.name ?? absent("unnamed")}</Fact>
          <Fact label="Type" field="property_type">{propertyType(p.property_type)}</Fact>
          <Fact label="Units" field="unit_count">{p.unit_count ?? absent()}</Fact>
          <Fact label="Parking spaces" field="parking_spaces">{p.parking_spaces ?? absent()}</Fact>
          <Fact label="Existing chargers" field="has_existing_ev_chargers">{yesNo(p.has_existing_ev_chargers)}</Fact>
          <Fact label="Affordable" field="affordable_flag">{yesNo(p.affordable_flag)}</Fact>
          <Fact label="Designations">{p.designations?.join(", ") || absent()}</Fact>
          <Fact label="Owner"><RecordLink to="companies" id={p.owner_company_id} label={p.owner_company_name} /></Fact>
          <Fact label="Developer"><RecordLink to="companies" id={p.developer_company_id} label={p.developer_company_name} /></Fact>
        </FactGroup>

        <FactGroup title="What EVIE knows">
          {editing ? null : <LookupFacts token={token} entityType="property" entityId={id} {...toolProps("lookup")} />}
          {editing ? null : <FlagFacts token={token} entityType="property" entityId={id} {...toolProps("flag")} />}
          <Fact label="Sources">{p.source_count ?? absent()}</Fact>
          <Fact label="Source tiers">{p.source_tiers?.join(", ") || absent()}</Fact>
          <Fact label="Signals">{p.signal_count ?? absent()}</Fact>
          <Fact label="Newest source">{shortDate(p.newest_source_date)}</Fact>
          <Fact label="First seen">{shortDate(p.created_at)}</Fact>
          <Fact label="Last changed">{shortDate(p.updated_at)}</Fact>
          <Fact label="Id">{p.id}</Fact>
        </FactGroup>
      </Facts>
    </section>
  );
}

const PROJECT_FILTERS = ["stage"] as const;

function ProjectsTab({ token, id }: { token: string; id: string }) {
  const params = useListParams("newest_source_date", PROJECT_FILTERS, false, "", "pj.");
  const stage = params.filters["stage"] ?? "";
  const [filtersOpen, setFiltersOpen] = useFiltersOpen(Boolean(stage));
  const { words, orderOf } = useStageOrder(token);
  const q = safe(params.search);
  const projects = useQuery({
    queryKey: ["property-projects", id, params.sorts, q, stage],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let query = apiClient(token)
        .from("project")
        .select("id,name,kind,status,stage_word,unit_count,announced_at,groundbreaking_at,est_completion,person_count,newest_source_date")
        .eq("property_id", id);
      if (q) query = query.ilike("name", `*${q}*`);
      if (stage) query = query.eq("stage_word", stage);
      const { data, error } = await applySorts(query, params.sorts).order("id", { ascending: true }).limit(100);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Project[];
    },
  });
  const rows = projects.data ?? [];
  const columns: ColumnSpec<Project>[] = [
    { id: "name", header: "Project", role: "leading", fields: ["name"], sort: "name", title: true, narrowLabel: null, cell: (pj) => orAbsent(pj.name) },
    { id: "stage", header: "Stage", role: "leading", fields: ["stage_word"], sort: "stage_word", narrowLabel: null,
      cell: (pj) => <Stage word={pj.stage_word} order={orderOf(pj.stage_word)} /> },
    { id: "kind", header: "Type", role: "secondary", fields: ["kind"], sort: "kind", narrowLabel: null, cell: (pj) => orAbsent(pj.kind) },
    { id: "units", header: "Units", role: "secondary", fields: ["unit_count"], align: "num", sort: "unit_count", cell: (pj) => pj.unit_count ?? absent() },
    { id: "announced", header: "Announced", role: "secondary", fields: ["announced_at"], sort: "announced_at", sortDescendingFirst: true, cell: (pj) => shortDate(pj.announced_at) },
    { id: "groundbreaking", header: "Groundbreaking", role: "detail", fields: ["groundbreaking_at"], sort: "groundbreaking_at", sortDescendingFirst: true, cell: (pj) => shortDate(pj.groundbreaking_at) },
    { id: "completion", header: "Expected finish", role: "secondary", fields: ["est_completion"], sort: "est_completion", sortDescendingFirst: true, cell: (pj) => shortDate(pj.est_completion) },
    { id: "people", header: "People", role: "detail", fields: ["person_count"], align: "num", sort: "person_count", cell: (pj) => pj.person_count ?? absent() },
  ];
  const chips: Chip[] = stage ? [{ label: `Stage: ${stage}`, onRemove: () => params.set({ filters: {} }) }] : [];
  const narrowed = chips.length > 0 || Boolean(q);
  return (
    <section className="rp-tabbody" aria-label="Projects">
      <Toolbar
        id="projects"
        search={{ label: "Search by project", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        filtersCount={chips.length} filtersOpen={filtersOpen} onToggleFilters={() => setFiltersOpen((v) => !v)}
        chips={chips} onClearAll={() => params.set({ search: "", filters: {} })}
        right={rows.length ? (
          <Button size="compact-sm" onClick={() => downloadCsv(csvFilename("property-projects"),
            toCsv(["name", "stage_word", "kind", "unit_count", "announced_at", "groundbreaking_at", "est_completion"], rows as unknown as Record<string, unknown>[]))}>
            CSV
          </Button>
        ) : null}
      />
      <FiltersPanel id="projects" open={filtersOpen} onDone={() => setFiltersOpen(false)}>
        <div className="filters-own">
          <NativeSelect label="Stage" value={stage} onChange={(e) => params.set({ filters: { stage: e.currentTarget.value } })}
            data={[{ value: "", label: "any" }, ...words.map((w) => ({ value: w.word, label: w.word }))]} />
        </div>
      </FiltersPanel>
      {projects.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : projects.isError ? <p className="refusal">{(projects.error as Error).message}</p>
        : !rows.length ? (
          <p className="muted">
            {narrowed
              ? "No project matches these filters."
              : <>No projects on record. A project arrives from a permit, a filing or a news page the engine reads, and <Link to="?tab=details">Run web enrichment</Link> asks for a search.</>}
          </p>
        )
        : <DataTable columns={columns} rows={rows} rowKey={(pj) => pj.id ?? ""} params={params} />}
    </section>
  );
}

function CompaniesTab({ token, id }: { token: string; id: string }) {
  const params = useListParams("company_name", [], true, "", "c.");
  const nav = useRecordNav();
  const q = safe(params.search);
  const companies = useQuery({
    queryKey: ["property-stakeholders", id, params.sorts, q],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // One row per company, its roles together, the same view the company
      // page reads. Ended relationships stay, because who used to own a
      // building is worth knowing here.
      let query = apiClient(token)
        .from("company_property")
        .select("company_id,company_name,company_kind,roles,ended_roles,standing,last_confirmed_at")
        .eq("property_id", id);
      if (q) query = query.ilike("company_name", `*${q}*`);
      const { data, error } = await applySorts(query, params.sorts).order("company_id", { ascending: true }).limit(100);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as CompanyProperty[];
    },
  });
  const rows = companies.data ?? [];
  const columns: ColumnSpec<CompanyProperty>[] = [
    { id: "company", header: "Company", role: "leading", fields: ["company_name"], sort: "company_name", title: true, narrowLabel: null,
      cell: (s) => <RecordLink to="companies" id={s.company_id} label={s.company_name} /> },
    { id: "role", header: "Role", role: "leading", fields: ["roles"], narrowLabel: null,
      cell: (s) => roleWords(s.roles?.length ? s.roles : s.ended_roles) || absent() },
    { id: "kind", header: "Type", role: "secondary", fields: ["company_kind"], sort: "company_kind", narrowLabel: null, cell: (s) => orAbsent(s.company_kind?.replace(/_/g, " ")) },
    { id: "standing", header: "Standing", role: "secondary", fields: ["standing"], sort: "standing", narrowLabel: null, cell: (s) => <StandingWord standing={s.standing} /> },
    { id: "confirmed", header: "Confirmed", role: "secondary", fields: ["last_confirmed_at"], sort: "last_confirmed_at", sortDescendingFirst: true, cell: (s) => shortDate(s.last_confirmed_at) },
  ];
  return (
    <section className="rp-tabbody" aria-label="Companies">
      <Toolbar
        id="companies"
        search={{ label: "Search by company", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        onClearAll={() => params.set({ search: "" })}
        right={rows.length ? (
          <Button size="compact-sm" onClick={() => downloadCsv(csvFilename("property-companies"),
            toCsv(["company_name", "company_kind", "roles", "standing", "last_confirmed_at"],
                  rows.map((s) => ({ ...s, roles: roleWords(s.roles?.length ? s.roles : s.ended_roles) }))))}>
            CSV
          </Button>
        ) : null}
      />
      <p className="muted rp-count">Every company with a role on this property, ended ones included.</p>
      {companies.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : companies.isError ? <p className="refusal">{(companies.error as Error).message}</p>
        : !rows.length ? (
          <p className="muted">
            {q
              ? "No company matches that search."
              : <>No companies on record. An owner or a manager reaches a property through a filing, a permit or a page the engine reads, and <Link to="?tab=details">Run web enrichment</Link> asks for a search.</>}
          </p>
        )
        : <DataTable columns={columns} rows={rows} rowKey={(s) => s.company_id ?? ""} params={params}
                     openId={nav?.lastOpened}
                     onOpen={(s) => { if (s.company_id) nav?.open(`/companies/${s.company_id}`, s.company_id); }} />}
    </section>
  );
}

/* Who to call at this property, from the engine's one rule
   (api.property_contacts, engine migrations 232 and 233, docs/campaign-people.md
   there). Every person connected to the property is scored from 0 to 100
   with the reasons in words, and the people above the line come first, in
   call order. With no campaign in the address the rule reads the property's
   own segment and the default cut. Opened from a campaign's List tab
   (?campaign=), it shows that campaign's picks first, as they were taken,
   with when. No sorting here: the order is the engine's. */

type PropertyContact = {
  call_order: number | null; person_id: string | null; full_name: string | null; title: string | null;
  role_category: string | null; level: string | null; employer_id: string | null; employer_name: string | null;
  basis: string | null; company_role: string | null; project_role: string | null; email: string | null;
  mobile: string | null; phone: string | null; linkedin_url: string | null; others_in_category: number | null;
  advises: boolean | null; fit: number | null; reasons: string[] | null; above_line: boolean | null;
  rank_in_property: number | null; taken_at: string | null;
};

function PeopleTab({ token, id }: { token: string; id: string }) {
  const [search] = useSearchParams();
  const campaign = search.get("campaign");
  const labels = useCampaignLabels(token);
  const params = useListParams("rank", [], false, "", "w.");
  const q = params.search.trim().toLowerCase();
  const people = useQuery({
    queryKey: ["property-contacts", id, campaign],
    placeholderData: keepPreviousData,
    queryFn: () => callFunction<PropertyContact[]>(token, "property_contacts",
      { p_property_id: id, ...(campaign ? { p_campaign: campaign } : {}) }),
  });
  const all = people.data ?? [];
  const matches = (c: PropertyContact) => !q
    || [c.full_name, c.title, c.employer_name].some((v) => (v ?? "").toLowerCase().includes(q));
  const first = all.filter((c) => c.above_line && matches(c));
  const rest = all.filter((c) => !c.above_line && matches(c));
  const taken = all.find((c) => c.taken_at)?.taken_at ?? null;
  const columns: ColumnSpec<PropertyContact>[] = [
    { id: "order", header: "#", role: "leading", fields: [], align: "num", narrowLabel: null,
      cell: (c) => c.call_order ?? c.rank_in_property ?? absent() },
    { id: "name", header: "Name", role: "leading", fields: ["full_name"], title: true, narrowLabel: null,
      cell: (c) => <RecordLink to="people" id={c.person_id} label={c.full_name} /> },
    { id: "title", header: "Title", role: "leading", fields: ["title"], narrowLabel: null, cell: (c) => orAbsent(c.title) },
    { id: "employer", header: "Employer", role: "secondary", fields: ["employer_name"], narrowLabel: null,
      cell: (c) => <RecordLink to="companies" id={c.employer_id} label={c.employer_name} /> },
    /* Never print a contact without its basis. An unlabelled company contact
       in the first slot is indistinguishable from a researched one. */
    { id: "basis", header: "Tied to", role: "secondary", fields: ["basis"], narrowLabel: null,
      cell: (c) => c.basis === "project"
        ? <Word tone="ok" title="A source tied this person to this property">this property</Word>
        : <Word tone="quiet" title="Works at a company with a role on this property">the company</Word> },
    { id: "fit", header: "Fit", role: "secondary", fields: ["fit"], align: "num", narrowLabel: "fit",
      cell: (c) => c.fit ?? absent() },
    { id: "why", header: "Why", role: "detail", fields: ["reasons"], narrowLabel: null,
      cell: (c) => (c.reasons ?? []).join(". ") || absent() },
    { id: "reach", header: "Reach", role: "secondary", fields: ["mobile"], narrowLabel: null,
      cell: (c) => <ReachCell p={c} /> },
  ];
  const csv = (rows: PropertyContact[]) => toCsv(
    ["call_order", "full_name", "title", "employer_name", "basis", "fit", "reasons", "email", "mobile", "phone", "linkedin_url"],
    rows.map((c) => ({ ...c, reasons: (c.reasons ?? []).join("; ") })) as unknown as Record<string, unknown>[]);
  return (
    <section className="rp-tabbody" aria-label="Who to call">
      <Toolbar
        id="who"
        search={{ label: "Search by name, title or employer", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        onClearAll={() => params.set({ search: "" })}
        right={all.length ? (
          <Button size="compact-sm" onClick={() => downloadCsv(csvFilename("who-to-call"), csv([...first, ...rest]))}>
            CSV
          </Button>
        ) : null}
      />
      <p className="muted rp-count">
        {campaign
          ? <>For <Link to={campaignPath(campaign)}>{campaignWords(campaign, labels.data)}</Link>{taken ? <>, chosen {shortDate(taken)}</> : <>, by the campaign's rule read now</>}. The people above the campaign's line first, in call order, then everyone else connected.</>
          : <>The people to call first, by this property's own rule, then everyone else connected. A campaign's own picks show from its list.</>}
      </p>
      {people.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : people.isError ? <p className="refusal">{(people.error as Error).message}</p>
        : !all.length ? (
          <p className="muted">
            No person is connected to this property yet. People reach a property through its companies and its projects, so the <Link to="?tab=companies">Companies tab</Link> is where to look, and Run Apollo enrichment on a company adds people in buying roles.
          </p>
        )
        : !first.length && !rest.length ? <p className="muted">No person matches that search.</p>
        : (
          <>
            <h3 className="rp-sub">Call first <span className="muted">{first.length.toLocaleString()}</span></h3>
            {first.length
              ? <DataTable columns={columns} rows={first} rowKey={(c) => c.person_id ?? ""} params={params} />
              : <p className="muted">No person is above the line at this property. Everyone connected is below.</p>}
            <h3 className="rp-sub">Everyone else connected <span className="muted">{rest.length.toLocaleString()}</span></h3>
            {rest.length
              ? <DataTable columns={columns} rows={rest} rowKey={(c) => c.person_id ?? ""} params={params} />
              : <p className="muted">That is everyone.</p>}
          </>
        )}
    </section>
  );
}
